/**
 * Design System - DataTable Component
 * Tabella standardizzata per dati
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export interface Column<T = any> {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface DataTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  error?: string;
  page?: number;
  rowsPerPage?: number;
  totalRows?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onSort?: (columnId: string, direction: 'asc' | 'desc') => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  emptyMessage?: string;
  hover?: boolean;
  striped?: boolean;
}

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  '& .MuiTable-root': {
    '& .MuiTableHead-root': {
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        borderBottom: `2px solid ${theme.palette.divider}`,
        padding: '12px 16px',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        '&:hover': {
          backgroundColor: theme.palette.grey[50],
        },
        '&:nth-of-type(even)': {
          backgroundColor: theme.palette.grey[25],
        },
      },
    },
  },
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'hover' && prop !== 'striped',
})<{ hover?: boolean; striped?: boolean }>(
  ({ theme, hover, striped }) => ({
    '&:hover': hover ? {
      backgroundColor: theme.palette.grey[50],
    } : {},
    '&:nth-of-type(even)': striped ? {
      backgroundColor: theme.palette.grey[25],
    } : {},
  })
);

const LoadingRow = styled(TableRow)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
}));

const EmptyRow = styled(TableRow)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

export const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  error,
  page = 0,
  rowsPerPage = 10,
  totalRows = 0,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortBy,
  sortDirection = 'asc',
  emptyMessage = 'Nessun dato disponibile',
  hover = true,
  striped = true,
}: DataTableProps<T>) => {
  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange?.(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange?.(parseInt(event.target.value, 10));
  };

  const handleSort = (columnId: string) => {
    if (!onSort) return;
    
    const isCurrentSort = sortBy === columnId;
    const newDirection = isCurrentSort && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(columnId, newDirection);
  };

  const renderCellContent = (column: Column<T>, row: T) => {
    const value = row[column.id];
    
    if (column.format) {
      return column.format(value, row);
    }
    
    return value;
  };

  if (error) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" variant="body1">
          Errore nel caricamento dei dati: {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <StyledTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                  onClick={() => column.sortable && handleSort(column.id)}
                  sx={{
                    cursor: column.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': column.sortable ? {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    } : {},
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {column.label}
                    </Typography>
                    {column.sortable && sortBy === column.id && (
                      <Typography variant="caption" color="primary">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Caricamento dati...
                    </Typography>
                  </Box>
                </TableCell>
              </LoadingRow>
            ) : data.length === 0 ? (
              <EmptyRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </EmptyRow>
            ) : (
              data.map((row, index) => (
                <StyledTableRow
                  key={index}
                  hover={hover}
                  striped={striped}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                    >
                      {renderCellContent(column, row)}
                    </TableCell>
                  ))}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      
      {totalRows > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default DataTable;
